import type { ComponentType, ReactElement, ReactNode } from 'react'

import type { SortingMode } from '@skytech/utils'
import type {
  ColumnDef,
  Header,
  OnChangeFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import ArrowCollapseLeftIcon from 'mdi-react/ArrowCollapseLeftIcon'
import ArrowCollapseRightIcon from 'mdi-react/ArrowCollapseRightIcon'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { Checkbox, Select, Table, Button, Td, SortingIcon, TrButton } from './index'

export const INITIAL_PER_PAGE_LIMIT = 30

type Pagination = {
  currentPage: number
  perPage: number
  totalPages: number
  totalCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

type BasicTableProps<T extends { id: string | number }> = {
  dataWithPagination?: {
    items: T[]
    pagination: Pagination
  }
  columns: ColumnDef<T>[]
  pagination: PaginationState
  setPagination?: OnChangeFn<PaginationState>
  sorting: SortingState
  setSorting?: OnChangeFn<SortingState>
  isSingleSelection?: boolean
  isMultipleSelection?: boolean
  selectedRows?: number[]
  onTrClick?: (value: number) => void
  selectAll?: {
    isAllSelected: boolean
    handleSelectAll: (value: boolean) => void
  }
  wrapperClassName?: string
  TranslationComponent: ComponentType<
    {
      id?: string
      className?: string
      children?: ReactNode
    } & Record<string, string | ReactElement>
  >
}

export const BasicTable = <T extends { id: number }>({
  dataWithPagination,
  columns,

  pagination = {
    pageIndex: 0,
    pageSize: INITIAL_PER_PAGE_LIMIT,
  },
  setPagination,

  sorting,
  setSorting,

  isSingleSelection = false,
  isMultipleSelection = false,
  selectedRows,
  onTrClick,
  selectAll,

  wrapperClassName,
  TranslationComponent,
}: BasicTableProps<T>) => {
  const TComponent = TranslationComponent
  const table = useReactTable<T>({
    data: dataWithPagination?.items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),

    // Sort config
    onSortingChange: setSorting,
    manualSorting: true,
    sortDescFirst: true,

    // Pagination config
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: dataWithPagination?.pagination.perPage,
    pageCount: dataWithPagination?.pagination.totalPages,
    manualPagination: true,
    state: {
      sorting,
      pagination,
    },
  })

  const sortToggler = (header: Header<T, unknown>) => {
    if (header.column.getCanSort()) {
      header.column.toggleSorting(undefined, true)
    }
  }

  return (
    <div className={twJoin(wrapperClassName)}>
      <Table rowHeight="short">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="flex items-center">
              {isMultipleSelection && selectAll && (
                <th className="md:p-2 lg:p-2.5 xl:p-4">
                  <Checkbox
                    id="table-select-all"
                    checked={selectAll.isAllSelected}
                    className="!-mt-2"
                    onChange={e => {
                      selectAll.handleSelectAll(e.target.checked)
                    }}
                  />
                </th>
              )}
              {headerGroup.headers.map(header => (
                <BasicTableTh
                  key={header.id}
                  id={header.id}
                  onClick={() => sortToggler(header)}
                  isSortable={header.column.getCanSort()}
                  sortMode={header.column.getIsSorted() || 'off'}
                  className="capitalize md:p-2 lg:p-2.5"
                >
                  {header.isPlaceholder ? null : (
                    <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                  )}
                </BasicTableTh>
              ))}
            </tr>
          ))}
        </thead>

        <tbody className="overflow-y-auto">
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <Td className="text-xl md:p-2 lg:p-2.5 xl:p-4">
                <TComponent id="No data found component" />
              </Td>
            </tr>
          ) : (
            <>
              {table.getRowModel().rows.map(row => {
                const onRowClick = (e?: React.ChangeEvent<HTMLInputElement>) => {
                  e?.preventDefault()
                  if (!onTrClick) return
                  if (isSingleSelection || isMultipleSelection) {
                    onTrClick(Number(row.original.id))
                  }
                }
                return (
                  <TrButton
                    key={row.id}
                    onClick={onRowClick}
                    className={twMerge(
                      (isSingleSelection || isMultipleSelection) &&
                        selectedRows?.includes(Number(row.original.id)) &&
                        'selected'
                    )}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Td
                        key={cell.id}
                        className="flex flex-row justify-start !rounded-none md:p-2 lg:p-2.5 xl:p-4"
                      >
                        {isMultipleSelection && (
                          <Checkbox
                            id={`table-select-${row.id}`}
                            checked={selectedRows?.includes(Number(row.original.id)) ?? false}
                            onChange={e => onRowClick(e)}
                          />
                        )}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </TrButton>
                )
              })}
            </>
          )}
        </tbody>
      </Table>

      <footer className="bg-white p-2">
        <div className="mx-1 mb-2 mt-4 flex flex-col justify-between gap-2">
          <div className="flex items-center justify-between gap-2">
            <span>
              <TComponent
                id="Page {pageIndex} of {totalPages}:"
                pageIndex={(table.getState().pagination.pageIndex + 1).toString()}
                totalPages={table.getPageCount().toString()}
              />
            </span>

            <Select
              id="page-size"
              label={<TComponent id="Rows per page:" />}
              value={table.getState().pagination.pageSize}
              className="flex flex-row items-center justify-start gap-2"
              onChange={option => {
                table.setPageSize(option?.value || INITIAL_PER_PAGE_LIMIT)
              }}
              options={[10, 30, 50, 100].map(option => ({
                value: option,
                label: String(option),
              }))}
            />
          </div>

          <div className="flex items-center justify-between gap-1">
            <Button
              className="ml-0"
              size="extraSmall"
              Icon={<ArrowCollapseLeftIcon />}
              onClick={() => table.setPageIndex(0)}
              disabled={Boolean(table.getState().pagination.pageIndex === 0)}
            />
            <Button
              size="extraSmall"
              Icon={<ArrowLeftIcon />}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            />
            <Button
              size="extraSmall"
              Icon={<ArrowRightIcon />}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            />
            <Button
              className="mr-0"
              size="extraSmall"
              Icon={<ArrowCollapseRightIcon />}
              onClick={() => table.setPageIndex(table.getPageCount())}
              disabled={Boolean(table.getState().pagination.pageIndex + 1 === table.getPageCount())}
            />
          </div>
        </div>
      </footer>
    </div>
  )
}

export const BasicTableTh = <T extends string>({
  id,
  children,
  className,
  isSortable = true,
  sortMode,
  onClick,
}: {
  id?: T
  children?: ReactNode
  className?: string
  isSortable?: boolean
  sortMode: SortingMode
  onClick: () => void
}) => {
  // Regular table header (no sorting)
  if (!isSortable || !id) return <th className={className}>{children}</th>

  return (
    <th className={className}>
      <button
        className="border-none bg-transparent text-base font-bold text-zinc-900 hover:cursor-pointer"
        onClick={onClick}
      >
        <div className="flex items-center gap-0.5">
          {children}
          <SortingIcon sortingMode={isSortable ? sortMode : 'off'} />
        </div>
      </button>
    </th>
  )
}
